/**
 * Entrada del lienzo: punteros (ratón, lápiz, dedo), gestos de dos dedos,
 * rueda/trackpad, teclado y registro de herramientas.
 */
import type { ToolId } from './state/store.ts'
import { MAX_ZOOM, MIN_ZOOM } from './state/store.ts'
import { clamp } from './lib/geom.ts'
import { DrawTool } from './tools/draw.ts'
import { EraserTool } from './tools/eraser.ts'
import { SelectTool } from './tools/select.ts'
import { NoteTool, ShapeTool, TextTool } from './tools/create.ts'
import { HandTool, LaserTool } from './tools/laser.ts'
import { isNativeInkActive, isNativePenDown, isNativePenRecent } from './native/bridge.ts'
import { hitTest } from './canvas/hit.ts'
import type { AppContext, PointerInfo, Tool } from './tools/types.ts'

interface ActivePointer {
  id: number
  sx: number
  sy: number
  type: 'mouse' | 'pen' | 'touch'
  /** dónde y cuándo bajó: para distinguir un toque de un arrastre */
  downSx: number
  downSy: number
  downAt: number
}

export class InputManager {
  private ctx: AppContext
  private el: HTMLElement
  private tools = new Map<ToolId, Tool>()
  private current: Tool
  private pointers = new Map<number, ActivePointer>()
  private capturedId: number | null = null
  private spaceDown = false
  private panning: { sx: number; sy: number; camX: number; camY: number } | null = null
  private gesture: { dist: number; cx: number; cy: number; camX: number; camY: number; z: number } | null = null
  // Menos infinito = "nunca": con 0, performance.now() recién cargada la página
  // haría creer que el lápiz acaba de escribir y se comería los primeros toques.
  private lastPenTime = Number.NEGATIVE_INFINITY
  private lastTapTime = 0
  private lastTapPos = { x: 0, y: 0 }
  private cursorRaf = 0

  constructor(ctx: AppContext, el: HTMLElement) {
    this.ctx = ctx
    this.el = el

    const select = new SelectTool(ctx, false)
    this.tools.set('select', select)
    this.tools.set('hand', new HandTool(ctx))
    this.tools.set('draw', new DrawTool(ctx, 'draw'))
    this.tools.set('highlighter', new DrawTool(ctx, 'highlighter'))
    this.tools.set('eraser', new EraserTool(ctx))
    this.tools.set('note', new NoteTool(ctx))
    this.tools.set('text', new TextTool(ctx))
    this.tools.set('shape', new ShapeTool(ctx))
    this.tools.set('laser', new LaserTool(ctx))
    this.current = select
    this.current.onActivate?.()

    el.addEventListener('pointerdown', this.onPointerDown)
    el.addEventListener('pointermove', this.onPointerMove)
    window.addEventListener('pointerup', this.onPointerUp)
    window.addEventListener('pointercancel', this.onPointerCancel)
    el.addEventListener('wheel', this.onWheel, { passive: false })
    el.addEventListener('contextmenu', this.onContextMenu)
    el.addEventListener('dblclick', this.onDoubleClick)
    window.addEventListener('keydown', this.onKeyDown)
    window.addEventListener('keyup', this.onKeyUp)
    el.addEventListener('touchstart', this.preventTouchScroll, { passive: false })
    el.addEventListener('touchmove', this.preventTouchScroll, { passive: false })
    // En TODO el documento, no solo el lienzo: la mano se asienta abajo, justo
    // sobre la barra de herramientas, y ahí WebKit hacía su selección azul y
    // la palma podía apretar botones a mitad de trazo.
    document.addEventListener('touchstart', this.blockPalmWhileWriting, { passive: false, capture: true })
  }

  /** Herramienta "lazo": es la de selección en modo lazo. */
  private lassoTool: SelectTool | null = null

  setTool(id: ToolId | 'lasso'): void {
    let next: Tool | undefined
    if (id === 'lasso') {
      if (!this.lassoTool) this.lassoTool = new SelectTool(this.ctx, true)
      next = this.lassoTool
    } else {
      next = this.tools.get(id)
    }
    if (!next || next === this.current) return
    this.current.onCancel?.()
    this.current.onDeactivate?.()
    this.current = next
    this.current.onActivate?.()
    this.updateCursor()
  }

  /** Hay un puntero capturado (arrastre, trazo o gesto en curso). */
  get isBusy(): boolean {
    return this.capturedId !== null || this.gesture !== null || this.panning !== null
  }

  get activeTool(): Tool {
    return this.current
  }

  private toPointerInfo(e: PointerEvent): PointerInfo {
    const rect = this.el.getBoundingClientRect()
    const sx = e.clientX - rect.left
    const sy = e.clientY - rect.top
    const world = this.ctx.renderer.screenToWorld(sx, sy)
    return {
      x: world.x,
      y: world.y,
      sx,
      sy,
      pressure: e.pressure,
      pointerType: (e.pointerType as PointerInfo['pointerType']) || 'mouse',
      shiftKey: e.shiftKey,
      altKey: e.altKey,
      ctrlKey: e.ctrlKey,
      metaKey: e.metaKey,
      button: e.button,
      pointerId: e.pointerId,
    }
  }

  /**
   * El lápiz solo se ignora aquí cuando la app de iPad confirmó que lo está
   * capturando ella. Con cualquier otra herramienta —o si el puente falla— el
   * lápiz se atiende como siempre y sirve para seleccionar, tocar y escribir.
   */
  private isNativePencil(e: PointerEvent): boolean {
    // Un toque que la web ya estaba siguiendo se atiende hasta que se levante:
    // si el lápiz empezó en "seleccionar" y saltó a la pluma, el nativo se
    // enciende A MITAD del toque (no puede tomarlo) y descartar el resto aquí
    // perdía el trazo y dejaba el lápiz "pegado" matando los gestos del dedo.
    return e.pointerType === 'pen' && isNativeInkActive() && !this.pointers.has(e.pointerId)
  }

  private onPointerDown = (e: PointerEvent): void => {
    if (this.isNativePencil(e)) return
    if (this.ctx.store.readOnly && e.button === 0) {
      // Solo lectura: cualquier puntero solo desplaza (dos dedos hacen zoom).
      if (e.pointerType === 'touch') {
        const info = this.toPointerInfo(e)
        this.pointers.set(e.pointerId, {
          id: e.pointerId,
          sx: info.sx,
          sy: info.sy,
          type: 'touch',
          downSx: info.sx,
          downSy: info.sy,
          downAt: performance.now(),
        })
        if (this.pointers.size >= 2) {
          this.startGesture()
          return
        }
      }
      this.startPan(e)
      return
    }
    const info = this.toPointerInfo(e)
    // Mientras el lápiz escribe (o justo después), un dedo es la palma. Vale
    // tanto la señal nativa del iPad como la del propio navegador.
    if (info.pointerType === 'touch' && this.penRecent()) return
    this.pointers.set(e.pointerId, {
      id: e.pointerId,
      sx: info.sx,
      sy: info.sy,
      type: info.pointerType,
      downSx: info.sx,
      downSy: info.sy,
      downAt: performance.now(),
    })

    if (info.pointerType === 'pen') {
      this.lastPenTime = performance.now()
      // El lápiz bajó: cualquier cosa que la palma hubiera empezado (un
      // desplazamiento, un gesto, un arrastre capturado) muere sin confirmar.
      this.freezeTouches(e.pointerId)
      // Con "el lápiz siempre escribe": en lienzo vacío y con una herramienta
      // pasiva, el lápiz pasa a la última pluma en vez de abrir un marco.
      this.maybeInkWithPen(info)
    }

    // Dos dedos: gesto de desplazar + zoom. Solo si AMBOS son dedos y no hay un
    // trazo en curso: si estás escribiendo con el lápiz y apoyas la mano, antes
    // se cancelaba el trazo a medias y se perdía lo dibujado.
    const soloDedos = Array.from(this.pointers.values()).every((pt) => pt.type === 'touch')
    if (this.pointers.size >= 2 && soloDedos) {
      if (this.capturedId !== null) {
        // Había un trazo con el dedo: se confirma en vez de tirarlo.
        this.current.onPointerUp(info)
        try {
          this.el.releasePointerCapture(this.capturedId)
        } catch {
          /* ya soltado */
        }
        this.capturedId = null
      }
      this.startGesture()
      return
    }
    // Un lápiz escribiendo manda sobre cualquier dedo que aparezca después.
    if (this.pointers.size >= 2 && !soloDedos) {
      if (info.pointerType === 'touch') {
        this.pointers.delete(e.pointerId)
        return
      }
    }

    // Rechazo de palma: el dedo solo navega, NUNCA edita — con cualquier
    // herramienta. Antes esto dependía de la herramienta activa y, como la app
    // vuelve sola a "seleccionar", la mano apoyada abría un marco y lo
    // seleccionaba todo. Las herramientas de creación puntual (nota, texto,
    // figura) sí aceptan el dedo: colocar una nota con el dedo es deliberado.
    const dedoNavega = info.pointerType === 'touch' && this.fingerOnlyNavigates()
    // Botón central o espacio: desplazamiento temporal.
    if (e.button === 1 || this.spaceDown || dedoNavega) {
      this.startPan(e)
      return
    }
    if (e.button === 2) return

    this.capturedId = e.pointerId
    this.el.setPointerCapture(e.pointerId)
    this.current.onPointerDown(info)
    // La mano cambia a puño mientras arrastras, como en cualquier lienzo.
    if (this.current.id === 'hand') this.el.style.cursor = 'grabbing'
    this.ctx.renderer.invalidateOverlay()
  }

  private onPointerMove = (e: PointerEvent): void => {
    if (this.isNativePencil(e)) return
    const info = this.toPointerInfo(e)
    const tracked = this.pointers.get(e.pointerId)
    if (tracked) {
      tracked.sx = info.sx
      tracked.sy = info.sy
    }
    if (e.pointerType === 'pen') this.lastPenTime = performance.now()

    this.ctx.onCursorMove?.(info)

    if (this.gesture && this.pointers.size >= 2) {
      this.updateGesture()
      return
    }
    if (this.panning) {
      const { camera } = this.ctx.store
      this.ctx.store.setCamera({
        x: this.panning.camX - (info.sx - this.panning.sx) / camera.z,
        y: this.panning.camY - (info.sy - this.panning.sy) / camera.z,
      })
      return
    }
    if (this.capturedId !== null && e.pointerId !== this.capturedId) return
    this.current.onPointerMove(info)
    if (this.capturedId === null) this.updateCursorThrottled(info)
  }

  private onPointerUp = (e: PointerEvent): void => {
    if (this.isNativePencil(e)) return
    this.detectDoubleTap(e)
    this.pointers.delete(e.pointerId)
    if (this.pointers.size < 2) this.gesture = null
    if (this.panning && this.pointers.size === 0) {
      this.panning = null
      this.updateCursor()
    }
    if (this.capturedId !== null && e.pointerId === this.capturedId) {
      this.capturedId = null
      try {
        this.el.releasePointerCapture(e.pointerId)
      } catch {
        /* el puntero ya se soltó */
      }
      this.current.onPointerUp(this.toPointerInfo(e))
      if (this.current.id === 'hand') this.updateCursor()
      this.ctx.renderer.invalidateOverlay()
    }
  }

  /**
   * Cancelar NO es soltar: iPadOS cancela el toque de la palma en cuanto la
   * clasifica, y antes eso pasaba por onPointerUp y CONFIRMABA el marco de
   * selección barrido por la mano. Aquí la herramienta se cancela sin confirmar
   * (cada una decide qué hacer: la pluma guarda el trazo, el marco se descarta).
   */
  private onPointerCancel = (e: PointerEvent): void => {
    if (this.isNativePencil(e)) return
    this.pointers.delete(e.pointerId)
    if (this.pointers.size < 2) this.gesture = null
    if (this.panning && this.pointers.size === 0) {
      this.panning = null
      this.updateCursor()
    }
    if (this.capturedId !== null && e.pointerId === this.capturedId) {
      this.capturedId = null
      try {
        this.el.releasePointerCapture(e.pointerId)
      } catch {
        /* el puntero ya se soltó */
      }
      this.current.onCancel?.()
      if (this.current.id === 'hand') this.updateCursor()
      this.ctx.renderer.invalidateOverlay()
    }
  }

  private startPan(e: PointerEvent): void {
    this.ctx.onUserCameraMove?.()
    const rect = this.el.getBoundingClientRect()
    const { camera } = this.ctx.store
    this.panning = {
      sx: e.clientX - rect.left,
      sy: e.clientY - rect.top,
      camX: camera.x,
      camY: camera.y,
    }
    this.el.style.cursor = 'grabbing'
  }

  private startGesture(): void {
    this.ctx.onUserCameraMove?.()
    const pts = Array.from(this.pointers.values())
    if (pts.length < 2) return
    const { camera } = this.ctx.store
    this.panning = null
    this.gesture = {
      dist: Math.hypot(pts[0].sx - pts[1].sx, pts[0].sy - pts[1].sy),
      cx: (pts[0].sx + pts[1].sx) / 2,
      cy: (pts[0].sy + pts[1].sy) / 2,
      camX: camera.x,
      camY: camera.y,
      z: camera.z,
    }
  }

  private updateGesture(): void {
    const g = this.gesture
    if (!g) return
    const pts = Array.from(this.pointers.values())
    if (pts.length < 2) return
    const dist = Math.hypot(pts[0].sx - pts[1].sx, pts[0].sy - pts[1].sy)
    const cx = (pts[0].sx + pts[1].sx) / 2
    const cy = (pts[0].sy + pts[1].sy) / 2
    const scale = g.dist > 0 ? dist / g.dist : 1
    const z = clamp(g.z * scale, MIN_ZOOM, MAX_ZOOM)
    // El punto del mundo bajo el centro del gesto debe quedarse quieto.
    const worldX = g.camX + g.cx / g.z
    const worldY = g.camY + g.cy / g.z
    this.ctx.store.setCamera({ x: worldX - cx / z, y: worldY - cy / z, z })
  }

  private onWheel = (e: WheelEvent): void => {
    e.preventDefault()
    this.ctx.onUserCameraMove?.()
    const rect = this.el.getBoundingClientRect()
    const sx = e.clientX - rect.left
    const sy = e.clientY - rect.top
    const { camera } = this.ctx.store
    const zoomGesture = e.ctrlKey || e.metaKey || this.ctx.store.wheelZooms
    if (zoomGesture) {
      const factor = Math.exp(-e.deltaY * (e.ctrlKey ? 0.01 : 0.0022))
      const z = clamp(camera.z * factor, MIN_ZOOM, MAX_ZOOM)
      const worldX = camera.x + sx / camera.z
      const worldY = camera.y + sy / camera.z
      this.ctx.store.setCamera({ x: worldX - sx / z, y: worldY - sy / z, z })
    } else {
      this.ctx.store.setCamera({
        x: camera.x + (e.shiftKey ? e.deltaY : e.deltaX) / camera.z,
        y: camera.y + (e.shiftKey ? 0 : e.deltaY) / camera.z,
      })
    }
  }

  private onDoubleClick = (e: MouseEvent): void => {
    const rect = this.el.getBoundingClientRect()
    const sx = e.clientX - rect.left
    const sy = e.clientY - rect.top
    const world = this.ctx.renderer.screenToWorld(sx, sy)
    const info: PointerInfo = {
      x: world.x, y: world.y, sx, sy,
      pressure: 0.5, pointerType: 'mouse',
      shiftKey: e.shiftKey, altKey: e.altKey, ctrlKey: e.ctrlKey, metaKey: e.metaKey,
      button: 0, pointerId: -1,
    }
    if (this.current.onDoubleClick) {
      const before = this.ctx.store.editingId
      this.current.onDoubleClick(info)
      if (this.ctx.store.editingId !== before) return
    }
    this.toggleOverview(info)
  }

  /** Alterna entre vista general (todo el contenido) y 100 % centrado en el punto. */
  /**
   * Doble toque con el dedo = alternar la vista general. Se decide al SOLTAR:
   * solo cuenta como toque si el dedo ni arrastró ni se quedó apretado, así que
   * desplazar el lienzo a toquecitos rápidos no dispara la vista por accidente.
   */
  private detectDoubleTap(e: PointerEvent): void {
    const tracked = this.pointers.get(e.pointerId)
    if (!tracked || tracked.type !== 'touch') return
    const now = performance.now()
    const fueToque =
      now - tracked.downAt < 260 &&
      Math.hypot(tracked.sx - tracked.downSx, tracked.sy - tracked.downSy) < 12
    if (!fueToque) {
      this.lastTapTime = 0
      return
    }
    const near = Math.hypot(tracked.sx - this.lastTapPos.x, tracked.sy - this.lastTapPos.y) < 30
    if (now - this.lastTapTime < 350 && near) {
      this.lastTapTime = 0
      this.toggleOverview(this.toPointerInfo(e))
      return
    }
    this.lastTapTime = now
    this.lastTapPos = { x: tracked.sx, y: tracked.sy }
  }

  private toggleOverview(info: PointerInfo): void {
    if (this.ctx.store.camera.z > 0.55) this.ctx.zoomToFit()
    else this.ctx.zoomTo(1, info.x, info.y)
  }

  private onContextMenu = (e: MouseEvent): void => {
    e.preventDefault()
    const rect = this.el.getBoundingClientRect()
    this.ctx.openContextMenu?.(e.clientX - rect.left, e.clientY - rect.top, e.clientX, e.clientY)
  }

  /**
   * Mientras el lápiz está tocando el cristal, ningún dedo puede interactuar
   * con NADA: ni botones de la barra, ni selección de texto, ni menús. La mano
   * apoyada aterriza donde quiere (normalmente sobre la barra de abajo) y no
   * debe poder hacer nada mientras se escribe.
   */
  private blockPalmWhileWriting = (e: TouchEvent): void => {
    if (!isNativePenDown() && !isNativePenRecent()) return
    // Solo importan los toques NUEVOS: e.touches incluye también al lápiz que
    // ya está escribiendo, y si se le mirara, la palma pasaría de contrabando.
    const nuevos = Array.from(e.changedTouches) as (Touch & { touchType?: string })[]
    if (nuevos.some((t) => t.touchType === 'stylus')) return
    if (isEditingField(e.target)) return
    e.preventDefault()
  }

  private preventTouchScroll = (e: TouchEvent): void => {
    if (e.touches.length === 0) return
    // Los toques del lápiz deben quedar "sin consumir" cuando los captura la app
    // de iPad: si la página los reclama, WebKit cancela el trazo nativo a medias.
    if (isNativeInkActive()) {
      const touches = Array.from(e.touches) as (Touch & { touchType?: string })[]
      if (touches.every((t) => t.touchType === 'stylus')) return
    }
    e.preventDefault()
  }

  private isInkTool(): boolean {
    return ['draw', 'highlighter', 'eraser'].includes(this.ctx.store.tool)
  }

  /** ¿El lápiz está escribiendo o acaba de hacerlo? (señal nativa o del navegador) */
  private penRecent(): boolean {
    return isNativePenRecent() || performance.now() - this.lastPenTime < 900
  }

  /**
   * ¿Este dedo solo puede navegar? Con el rechazo de palma encendido, el dedo
   * desplaza el lienzo en las herramientas de selección y de tinta: así la mano
   * apoyada no puede seleccionar, arrastrar ni borrar nada, esté la herramienta
   * que esté. Nota, texto, figura y demás siguen aceptando el dedo.
   */
  private fingerOnlyNavigates(): boolean {
    const { store } = this.ctx
    const herramienta = this.current.id
    const esSeleccion = herramienta === 'select' || herramienta === 'hand'
    if (this.isInkTool()) return store.palmRejection || store.touchPans
    if (esSeleccion) return store.palmRejection
    return false
  }

  /**
   * El lápiz tocó el cristal: los dedos que hubiera dejan de contar. La mano se
   * apoya cientos de milisegundos ANTES que la punta, así que lo que la palma
   * hubiera empezado (desplazar, un gesto, un arrastre) se cancela sin
   * confirmar. Es la sensación de Freeform: escribiendo, la mano no existe.
   */
  private freezeTouches(exceptId: number): void {
    for (const [id, pt] of this.pointers) {
      if (pt.type === 'touch' && id !== exceptId) this.pointers.delete(id)
    }
    this.gesture = null
    this.panning = null
    if (this.capturedId !== null && this.capturedId !== exceptId) {
      const dueno = this.pointers.get(this.capturedId)
      if (!dueno || dueno.type !== 'touch') {
        // El capturado era un dedo ya borrado del mapa: se cancela sin confirmar.
        this.current.onCancel?.()
        try {
          this.el.releasePointerCapture(this.capturedId)
        } catch {
          /* ya soltado */
        }
        this.capturedId = null
        this.ctx.renderer.invalidateOverlay()
      }
    }
  }

  /**
   * "El lápiz siempre escribe": si el lápiz baja sobre lienzo vacío con la
   * herramienta de seleccionar o la manito, se cambia solo a la última pluma.
   * Sobre una figura (o con una selección activa, por las manijas) no se toca
   * nada: el lápiz sigue sirviendo para seleccionar, mover y escalar.
   */
  private maybeInkWithPen(info: PointerInfo): void {
    const { store } = this.ctx
    if (!store.penAlwaysInks || store.readOnly) return
    if (this.current.id !== 'select' && this.current.id !== 'hand') return
    if (this.current === this.lassoTool) return
    if (store.selection.length > 0) return
    if (hitTest(this.ctx.shapes(), info.x, info.y, store.camera.z)) return
    this.ctx.setTool(store.lastInkTool)
  }

  private onKeyDown = (e: KeyboardEvent): void => {
    if (e.key === ' ' && !this.spaceDown && !isEditingField(e.target)) {
      this.spaceDown = true
      this.el.style.cursor = 'grab'
    }
    this.ctx.onKeyDown?.(e)
  }

  private onKeyUp = (e: KeyboardEvent): void => {
    if (e.key === ' ') {
      this.spaceDown = false
      this.updateCursor()
    }
  }

  updateCursor(): void {
    if (this.panning) return
    this.el.style.cursor = this.spaceDown ? 'grab' : this.current.cursor
  }

  private updateCursorThrottled(info: PointerInfo): void {
    if (this.cursorRaf) return
    this.cursorRaf = requestAnimationFrame(() => {
      this.cursorRaf = 0
      if (this.spaceDown || this.panning) return
      if (this.current instanceof SelectTool) {
        this.el.style.cursor = this.current.cursorFor(info.sx, info.sy)
      } else {
        this.el.style.cursor = this.current.cursor
      }
    })
  }

  destroy(): void {
    this.current.onDeactivate?.()
    this.el.removeEventListener('pointerdown', this.onPointerDown)
    this.el.removeEventListener('pointermove', this.onPointerMove)
    window.removeEventListener('pointerup', this.onPointerUp)
    window.removeEventListener('pointercancel', this.onPointerCancel)
    document.removeEventListener('touchstart', this.blockPalmWhileWriting, { capture: true })
    this.el.removeEventListener('wheel', this.onWheel)
    this.el.removeEventListener('contextmenu', this.onContextMenu)
    this.el.removeEventListener('dblclick', this.onDoubleClick)
    window.removeEventListener('keydown', this.onKeyDown)
    window.removeEventListener('keyup', this.onKeyUp)
  }
}

export function isEditingField(target: EventTarget | null): boolean {
  const el = target as HTMLElement | null
  if (!el) return false
  const tag = el.tagName
  return tag === 'INPUT' || tag === 'TEXTAREA' || el.isContentEditable
}
